import { useMemo, useState } from 'react';
import { requireSatellite } from './config';
import { SatelliteLayout } from './layout';

const CONFIG = requireSatellite('courtreadyrecords.com');

type Situation = 'custody' | 'support' | 'relocation';

const SITUATIONS: Record<Situation, { label: string; items: string[] }> = {
  custody: {
    label: 'Custody modification',
    items: [
      'Current custody order or parenting plan',
      'Proposed schedule and a written reason for the change',
      'Exchange / visitation log for the last 6–12 months',
      'School, medical, and extracurricular records',
      'Messages about missed exchanges, pickups, or schedule disputes',
      'Any police, CPS, or protective-order records if they apply',
      'A list of witnesses and what each can speak to',
    ],
  },
  support: {
    label: 'Child-support dispute',
    items: [
      'Current support order and payment history',
      'Pay stubs or tax returns for both parents if available',
      'Childcare, health-insurance, and uncovered medical receipts',
      'School and activity expense records',
      'A ledger of what was paid, by whom, and when',
      'Messages about reimbursement requests',
      'Any prior enforcement or contempt filings',
    ],
  },
  relocation: {
    label: 'Relocation',
    items: [
      'Current custody order and any relocation clause',
      'The proposed move date, city, and reason',
      'A proposed long-distance parenting schedule',
      'School and housing information at the new location',
      'Travel-cost estimates and how exchanges would work',
      'Notice you already gave the other parent, if any',
      'Messages or emails about the proposed move',
    ],
  },
};

export function CourtReadyRecordsPage() {
  const [situation, setSituation] = useState<Situation>('custody');
  const items = SITUATIONS[situation].items;
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const keyPrefix = situation;
  const done = useMemo(
    () => items.filter((item) => checked[`${keyPrefix}:${item}`]).length,
    [checked, items, keyPrefix],
  );

  function toggle(item: string) {
    const key = `${keyPrefix}:${item}`;
    setChecked((current) => ({ ...current, [key]: !current[key] }));
  }

  return (
    <SatelliteLayout config={CONFIG}>
      <p className='text-sm text-gray-500'>
        A starting list only. This is not legal advice and does not replace a court-required form
        or your attorney.
      </p>

      <label className='mt-4 block text-sm print:hidden'>
        <span className='mb-1 block font-medium'>Situation</span>
        <select
          className='w-full rounded-lg border border-gray-300 px-3 py-2 sm:w-80'
          value={situation}
          onChange={(event) => setSituation(event.target.value as Situation)}
        >
          {Object.entries(SITUATIONS).map(([value, meta]) => (
            <option key={value} value={value}>
              {meta.label}
            </option>
          ))}
        </select>
      </label>

      <h2 className='mt-6 text-lg font-semibold'>
        {SITUATIONS[situation].label} checklist
        <span className='ml-2 text-sm font-normal text-gray-500'>
          {done} of {items.length}
        </span>
      </h2>

      <ul className='mt-4 space-y-3'>
        {items.map((item) => {
          const key = `${keyPrefix}:${item}`;
          return (
            <li key={item}>
              <label className='flex items-start gap-3 rounded-xl border border-gray-200 px-3 py-2'>
                <input
                  type='checkbox'
                  className='mt-1'
                  checked={Boolean(checked[key])}
                  onChange={() => toggle(item)}
                />
                <span>{item}</span>
              </label>
            </li>
          );
        })}
      </ul>

      <button
        type='button'
        className='mt-6 rounded-xl bg-primary px-4 py-2 font-semibold text-white hover:bg-primary-hover print:hidden'
        onClick={() => window.print()}
      >
        Print checklist
      </button>
    </SatelliteLayout>
  );
}
